/* eslint-disable react/jsx-pascal-case */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Button } from '@totalsoft/rocket-ui';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import { Prompt } from 'react-router-dom';
import { RightsContext } from 'providers/RightsContext';
import OfferFinancialServicesItemHeaderComponentExtendedFields from './OfferFinancialServicesItemHeaderComponentExtendedFields';
import OfferFinancialServicesItemComponentExtendedFields from './OfferFinancialServicesItemComponentExtendedFields';
import { OfferFinancialServicesAddServicesContainer } from './OfferFinancialServicesAddServicesContainer';
import OfferFinancialServicesHeaderComponentExtendedFields from './OfferFinancialServicesHeaderComponentExtendedFields';
import { getIsContractModification } from 'selectors/dictionariesSelectors';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper.js';
import { GridActionsWrapper } from 'components/common/CommonStyles.js';
import WrappedFakeText from 'components/common/WrappedFakeText.jsx';

export const OfferFinancialServicesListComponentExtendedFields = ({
  validationInfo,
  t,
  documentId,
  siteId,
  onServiceSelectionChange,
  onServiceItemPropertyChange,
  onServiceItemComputeValue,
  onCloseAvailableServicesModal,
  deleteService,
  showModal,
  onAddServices,
  saving,
  isDirty,
  onSave,
  servicesLoading,
  documentLoaded,
  document,
  editable,
  handleModifyData,
  currencyCode,
  onHeaderPropertyChange,
  offerTypeId,
  handleCancelModifyData,
  allowedContractTypesForAddingServices
}) => {
  const handleServiceSelectionChange = (serviceTypeId, serviceCategoryId, event) => {
    const selected = event.target.checked;
    onServiceSelectionChange(serviceTypeId, serviceCategoryId, selected);
  };

  const handleServiceItemPropertyChange = (serviceTypeId, serviceCategoryId, propName, value) => {
    onServiceItemPropertyChange(serviceTypeId, serviceCategoryId, propName, value);
  };

  const handleServiceItemComputeValue = service => () => {
    onServiceItemComputeValue(service);
  };

  const isContractModification = getIsContractModification(offerTypeId);

  const allowFinancialServicesToBeAdded = (documentContractCode, allowedContractCodes) => {
    const allowedContractCodesArray = allowedContractCodes.split(';');
    return allowedContractCodesArray.some(x => x === documentContractCode);
  };

  const addFinancialServicesEnabled = allowFinancialServicesToBeAdded(
    document.header.contractTypeCode,
    allowedContractTypesForAddingServices
  );

  const handleValidationInfo = (validationInfo, indexService, indexCategory) => {
    if (
      typeof indexService !== 'undefined' &&
      typeof indexCategory !== 'undefined' &&
      typeof validationInfo.categoryServices !== 'undefined' &&
      Object.keys(validationInfo.categoryServices).length !== 0 &&
      typeof validationInfo.categoryServices[indexCategory] !== 'undefined' &&
      Object.keys(validationInfo.categoryServices[indexCategory].services).length !== 0
    )
      return validationInfo.categoryServices[indexCategory].services[indexService];
  };

  const { readOnlyFull, financialServicesWrite, isImage, addDeleteAdditionalServices } = useContext(RightsContext);

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      {isContractModification && (
        <OfferFinancialServicesHeaderComponentExtendedFields
          header={document.header}
          onHeaderPropertyChange={onHeaderPropertyChange}
          validationInfo={validationInfo}
          offerTypeId={offerTypeId}
        />
      )}
      {documentLoaded ? (
        <CardNoResultsWrapper content={document?.services}>
          <Grid>
            {document.categoryServices.length > 0 &&
              document.categoryServices.map((category, indexCategory) => {
                if (category.services.length > 0)
                  return (
                    <CollapseOrBlankCard
                      sx={{ mb: 2 }}
                      id={'offerFinancialServicesCard_itemCard_' + indexCategory}
                      key={category.serviceCategoryId}
                      expanded={false}
                      canExpand={financialServicesWrite}
                      subheader={
                        <OfferFinancialServicesItemHeaderComponentExtendedFields
                          idx={indexCategory}
                          servicesLoading={servicesLoading}
                          category={category}
                          readOnlyFull={readOnlyFull}
                          onChange={handleServiceSelectionChange}
                          financialServicesWrite={financialServicesWrite}
                          currencyCode={currencyCode}
                        />
                      }
                    >
                      <Grid container spacing={2}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <strong>{t('OfferEdit.FinancialServices.ServiceName')}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>{`${t('OfferEdit.FinancialServices.TotalValue')} (${currencyCode})`}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>{`${t('OfferEdit.FinancialServices.MonthlyValue')} (${currencyCode})`}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>{t('OfferEdit.FinancialServices.Percent')}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>{t('OfferEdit.FinancialServices.Periodicity')}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>{t('OfferEdit.FinancialServices.FromInstallment')}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>{t('OfferEdit.FinancialServices.ToInstallment')}</strong>
                              </TableCell>
                              <TableCell>
                                <strong>{t('OfferEdit.FinancialServices.Period')}</strong>
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {category.services.map((service, index) => {
                              return (
                                <OfferFinancialServicesItemComponentExtendedFields
                                  categoryIndex={indexCategory}
                                  itemIndex={index}
                                  validationInfo={handleValidationInfo(validationInfo, index, indexCategory) || {}}
                                  key={service.serviceTypeId}
                                  item={service}
                                  readOnlyFull={readOnlyFull}
                                  category={category}
                                  loading={servicesLoading.some(x => x.serviceTypeId === service.serviceTypeId && x.value === true)}
                                  onChange={handleServiceSelectionChange}
                                  editable={readOnlyFull || editable}
                                  onServiceItemPropertyChange={handleServiceItemPropertyChange}
                                  deleteService={deleteService}
                                  onServiceItemComputeValue={handleServiceItemComputeValue(service)}
                                  saving={saving}
                                  disabled={!financialServicesWrite}
                                  currencyCode={currencyCode}
                                  addDeleteAdditionalServices={addDeleteAdditionalServices}
                                />
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Grid>
                    </CollapseOrBlankCard>
                  );
                else return <div></div>;
              })}
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>{t('OfferEdit.FinancialServices.ServiceName')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{`${t('OfferEdit.FinancialServices.TotalValue')} (${currencyCode})`}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{`${t('OfferEdit.FinancialServices.MonthlyValue')} (${currencyCode})`}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('OfferEdit.FinancialServices.Percent')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('OfferEdit.FinancialServices.Periodicity')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('OfferEdit.FinancialServices.FromInstallment')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('OfferEdit.FinancialServices.ToInstallment')}</strong>
                  </TableCell>
                  <TableCell>
                    <strong>{t('OfferEdit.FinancialServices.Period')}</strong>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {document.services.map((service, index) => {
                  return (
                    <OfferFinancialServicesItemComponentExtendedFields
                      itemIndex={index}
                      validationInfo={validationInfo.services[index] || {}}
                      key={service.serviceTypeId}
                      readOnlyFull={readOnlyFull}
                      item={service}
                      loading={servicesLoading.some(x => x.serviceTypeId === service.serviceTypeId && x.value === true)}
                      onChange={handleServiceSelectionChange}
                      editable={readOnlyFull || editable}
                      onServiceItemPropertyChange={handleServiceItemPropertyChange}
                      deleteService={deleteService}
                      onServiceItemComputeValue={handleServiceItemComputeValue(service)}
                      saving={saving}
                      disabled={!financialServicesWrite}
                      currencyCode={currencyCode}
                      addDeleteAdditionalServices={addDeleteAdditionalServices}
                    />
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
        </CardNoResultsWrapper>
      ) : (
        <WrappedFakeText lines={10} />
      )}
      {!isImage && (
        <GridActionsWrapper item xs={12} mt={2}>
          <OfferFinancialServicesAddServicesContainer
            showModal={showModal}
            onCloseAvailableServicesModal={onCloseAvailableServicesModal}
            availableUploadDocuments={[]}
            documentId={documentId}
            siteId={siteId}
          />
          <Button
            id={'offerFinancialServicesCard_AddBtn'}
            onClick={onAddServices}
            color='primary'
            variant='contained'
            disabled={
              readOnlyFull || !financialServicesWrite || editable || saving || !addFinancialServicesEnabled || !addDeleteAdditionalServices
            }
          >
            {t('Add')}
          </Button>

          <Button
            id={'offerFinancialServicesCard_saveBtn'}
            color='primary'
            variant='contained'
            onClick={onSave}
            disabled={
              readOnlyFull ||
              saving ||
              !validationInfo.isValid ||
              !isDirty ||
              !financialServicesWrite ||
              servicesLoading.some(x => x.value === true) ||
              document.services.some(x => x.canComputeService === true)
            }
          >
            {saving ? t('Saving') + '...' : t('Save')}
          </Button>
          {editable === false ? (
            <Button
              id={'offerFinancialServicesCard_modifyBtn'}
              onClick={handleModifyData}
              color='primary'
              variant='contained'
              disabled={
                readOnlyFull ||
                !financialServicesWrite ||
                saving ||
                (document.services.length === 0 && document.categoryServices.length === 0)
              }
            >
              {' '}
              {t('Change')}
            </Button>
          ) : (
            <Button
              id={'offerFinancialServicesCard_cancelModifyBtn'}
              onClick={handleCancelModifyData}
              color='primaryNoBackground'
              variant='outlined'
              disabled={
                readOnlyFull ||
                !financialServicesWrite ||
                saving ||
                (document.services.length === 0 && document.categoryServices.length === 0)
              }
            >
              {' '}
              {t('Cancel')}
            </Button>
          )}
        </GridActionsWrapper>
      )}
    </>
  );
};

OfferFinancialServicesListComponentExtendedFields.propTypes = {
  validationInfo: PropTypes.object.isRequired,
  onServiceSelectionChange: PropTypes.func.isRequired,
  onHeaderPropertyChange: PropTypes.func.isRequired,
  onCloseAvailableServicesModal: PropTypes.func.isRequired,
  onAddServices: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  wheelSizes: PropTypes.array.isRequired,
  saving: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  servicesLoading: PropTypes.array.isRequired,
  documentLoaded: PropTypes.bool.isRequired,
  document: PropTypes.object.isRequired,
  onServiceItemPropertyChange: PropTypes.func.isRequired,
  onServiceItemComputeValue: PropTypes.func.isRequired,
  editable: PropTypes.bool.isRequired,
  handleModifyData: PropTypes.func.isRequired,
  handleCancelModifyData: PropTypes.func.isRequired,
  deleteService: PropTypes.func.isRequired,
  currencyCode: PropTypes.string,
  offerTypeId: PropTypes.number.isRequired,
  allowedContractTypesForAddingServices: PropTypes.string.isRequired
};

export default withTranslation()(OfferFinancialServicesListComponentExtendedFields);
